import { inject } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { type WritableDraft } from 'immer';
import { first, map, type Observable, of, switchMap, timeout } from 'rxjs';

import { runtimeActions } from '@blaze-runtime/engine-data-access';
import {
  type CanvasElementFrom,
  type CanvasElementId,
  type PartialPropsOf,
} from '@blaze-runtime/engine-domain';

import { ElementsFacade } from '../../../+state/elements';
import { type EngineState } from '../../../+state/engine';
import { RuntimeFacade } from '../../../+state/runtime/runtime.facade';
import {
  AbstractAiElementHandler,
  bindValueToWorkflowSource,
  dispatchValueBindingRefresh,
  getInitialProps,
  upsertElement,
} from '../../../ai';
import { ComponentSourceWorkflowService } from '../../../ai/workflow';

import {
  type AvailabilityContext,
  type AvailabilityResolvedContext,
} from './availability.definition';

export const SUBMIT_WORKFLOW_TIMEOUT_MS = 10_000;

export class AvailabilityElementHandler extends AbstractAiElementHandler<'availability'> {
  private readonly componentSourceWorkflowService = inject(ComponentSourceWorkflowService);
  private readonly elementsFacade = inject(ElementsFacade);
  private readonly runtimeFacade = inject(RuntimeFacade);
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);

  readonly type = 'availability';

  prepare(context: AvailabilityContext): Observable<AvailabilityResolvedContext> {
    const entry = this.resolveWorkflowContext(context);

    if (!entry) {
      return of(context);
    }

    return this.componentSourceWorkflowService
      .ensureCreated(entry, context.projectId, context.pageId)
      .pipe(map((workflowContext) => ({ ...context, workflowContext })));
  }

  process(context: AvailabilityResolvedContext, draft: WritableDraft<EngineState>): void {
    const props: PartialPropsOf<'availability'> = getInitialProps<'availability'>(
      draft,
      context.element.id,
      {
        content: {},
      },
    );

    upsertElement(draft, context, props);

    const workflow = context.workflowContext?.workflow;
    if (!workflow) {
      return;
    }

    bindValueToWorkflowSource(draft, context, workflow);
    context.workflowsToFinalize = [{ workflow }];
  }

  finalize(
    element: CanvasElementFrom<'availability'>,
    context: AvailabilityResolvedContext,
  ): Observable<void> {
    if (!context.workflowContext) {
      return of(undefined);
    }

    return this.aiWorkflowGenerationService
      .handleAiGeneratedWorkflows(context.workflowsToFinalize ?? [])
      .pipe(switchMap(() => this.createSubmitWorkflow(element.id)));
  }

  private createSubmitWorkflow(elementId: CanvasElementId): Observable<void> {
    return dispatchValueBindingRefresh({
      elementsFacade: this.elementsFacade,
      runtimeFacade: this.runtimeFacade,
      store: this.store,
      elementId,
      timeoutMs: SUBMIT_WORKFLOW_TIMEOUT_MS,
      onTimeout: () =>
        console.warn(
          `Availability ${elementId}: value binding schema did not resolve, skipping submit workflow`,
        ),
      onError: (error) =>
        console.warn(`Availability ${elementId}: submit workflow creation failed`, error),
      afterDispatch: () => this.submitWorkflowCommitted(elementId),
    });
  }

  private submitWorkflowCommitted(elementId: CanvasElementId): Observable<void> {
    return this.actions$.pipe(
      ofType(runtimeActions.formSubmitActionUpdated),
      first(({ formConnection }) => formConnection.source.id === elementId),
      timeout({
        first: SUBMIT_WORKFLOW_TIMEOUT_MS,
        with: () => {
          console.warn(
            `Availability ${elementId}: submit workflow was not committed within ${SUBMIT_WORKFLOW_TIMEOUT_MS}ms`,
          );
          return of(undefined);
        },
      }),
      map(() => undefined),
    );
  }
}
